import { useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { DataLakeEnums, DataLakeQuery } from 'dashboard-services/api/dataLake'
import Query from 'dashboard-services/api/Query'
import { CommonService, ReduxService } from 'dashboard-services/services'
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder'
import { globalActions } from 'primary-components'

import { groupDialogDatalakeActions } from 'actions/modules/datalake'
import getApiConfig from 'api/ApiConfig'
import { filesDatalakeQueries, groupsDatalakeQueries } from 'queries/modules/datalake'

import groupConfigService from '../groupConfigService'
import groupDialogService from '../groupDialogService'

import { useQueryClient } from '@tanstack/react-query'

export default (() => {
  const { MODES, TABS } = groupDialogDatalakeActions

  const getValidationErrorsFromResponse = ({ message, response }) => response?.validationErrors || [{ field: "name", message }]

  const isFieldsError = error => !error?.field || String(error.field).startsWith("fields")

  const getFieldsErrorMessage = ({ error, fields }) => {
    const field = fields[String(error.field).replace("fields[", "").replace("]", "")]
    return field ? `Field ${field.name}: ${error.message}` : error.message
  }

  const useGroupErrors = ({ details, fieldsToSubmit, menuTabsRef }) => {
    const dispatch = useDispatch()
    return useCallback(({ message, response }) => {
      const validationErrors = getValidationErrorsFromResponse({ message, response })
      details.setErrors(validationErrors)
      menuTabsRef.current?.setSelectedTab(TABS.DETAILS)
      validationErrors
        .filter(isFieldsError)
        .forEach(error => dispatch(globalActions.getMessageStore()).error(getFieldsErrorMessage({ error, fields: fieldsToSubmit })))
    }, [details, dispatch, fieldsToSubmit, menuTabsRef])
  }

  const useCalls = ({ details, fieldsToSubmit, groupName, menuTabsRef }) => {
    const dispatch = useDispatch(),
          onGroupError = useGroupErrors({ details, fieldsToSubmit, menuTabsRef }),
          getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig())), [dispatch]),
          getGroupQueryBuilder = useCallback(() =>
            getQueryBuilder()
              .withShouldDisplayError(false)
              .withOnError(onGroupError)
          , [getQueryBuilder, onGroupError]),
          getShareQueryBuilder = useCallback(() =>
            getQueryBuilder()
              .withOnError(({ response = {} }) => dispatch(globalActions.getMessageStore()).error(response?.validationErrors?.[0]?.message || response?.message))
          , [dispatch, getQueryBuilder]),
          groupParams = { description: details.description, fields: fieldsToSubmit, groupTags: details.groupTags, getQueryBuilder: getGroupQueryBuilder },
          { mutateAsync: createGroup } = groupsDatalakeQueries.useCreateGroup(ReduxService.updateObject(groupParams, { name: groupName })),
          { mutateAsync: editGroup } = groupsDatalakeQueries.useEditGroup(ReduxService.updateObject(groupParams, { groupName })),
          { simpleUpload } = filesDatalakeQueries.useFileUpload({ getQueryBuilder, groupName, fileType: DataLakeEnums.FILE_TYPE.CONFIG }),
          { mutateAsync: shareGroup } = groupsDatalakeQueries.useShareGroup({ getQueryBuilder: getShareQueryBuilder }),
          { mutateAsync: unShareGroup } = groupsDatalakeQueries.useUnShareGroup({ getQueryBuilder: getShareQueryBuilder }),
          toReturn = useMemo(() => ({ createGroup, editGroup, simpleUpload, shareGroup, unShareGroup }), [createGroup, editGroup, shareGroup, simpleUpload, unShareGroup])

    return toReturn
  }

  const getCurrentDetails = ({ details, advanced }) => ({
    description: details.description,
    groupTags: details.groupTags,
    fields: details.fields,
    isDataTimeFrameEnabled: details.isDataTimeFrameEnabled,
    shouldBeForwardCurvesGroup: details.shouldBeForwardCurvesGroup,
    priority: advanced.priority
  })

  const useGroupDialogSubmit = ({ mode, groupName: openedGroupName, details, advanced, share, menuTabsRef }) => {
    const dispatch = useDispatch(),
          navigate = useNavigate(),
          queryClient = useQueryClient(),
          [created, setCreated] = useState(),
          isSubmitting = useRef(false),
          isCreate = mode === MODES.CREATE && !created,
          effectiveMode = created ? MODES.EDIT : mode,
          groupName = mode === MODES.CREATE ? (created?.groupName || String(details.groupName || "").trim()) : openedGroupName,
          baseDetails = useMemo(() => created?.details || ReduxService.updateObject(details.initialDetails, { priority: advanced.initialPriority }), [advanced.initialPriority, created?.details, details.initialDetails]),
          initialFields = created?.fields || details.initialDetails.fields,
          fieldsToSubmit = useMemo(() => groupDialogService.getFieldsToSubmit({
            fields: details.fields,
            initialFields,
            priority: advanced.priority,
            isDataTimeFrameEnabled: details.isDataTimeFrameEnabled
          }), [advanced.priority, details.fields, details.isDataTimeFrameEnabled, initialFields]),
          { createGroup, editGroup, simpleUpload, shareGroup, unShareGroup } = useCalls({ details, fieldsToSubmit, groupName, menuTabsRef }),

          uploadConfig = useCallback(() => Promise.all([
            simpleUpload({ image: JSON.stringify(groupConfigService.prepareGroupConfigForUpload(advanced.groupConfig)), fileName: `.${groupName}.json` }),
            simpleUpload({
              image: JSON.stringify(groupConfigService.cleanDynamicValidationParams({
                dynamicValidation: advanced.dynamicValidation,
                invalidParamNames: advanced.validatorTask.invalidParamNames,
                shouldRemoveInvalidParams: advanced.validationOptions.shouldRemoveInvalidParams
              })),
              fileName: `.${groupName}_ncsv_dynamic_validation.json`
            })
          ]), [advanced.dynamicValidation, advanced.groupConfig, advanced.validationOptions.shouldRemoveInvalidParams, advanced.validatorTask.invalidParamNames, groupName, simpleUpload]),
          saveShares = useCallback(() => Promise.all([
            share.shareChanges.addedOrChanged.length > 0 ? shareGroup({ sharesList: share.shareChanges.addedOrChanged }) : Promise.resolve(),
            share.shareChanges.removed.length > 0 ? unShareGroup({ sharesList: share.shareChanges.removed }) : Promise.resolve()
          ]), [share.shareChanges, shareGroup, unShareGroup]),
          onClose = useCallback(() => dispatch(groupDialogDatalakeActions.close()), [dispatch]),
          onSuccess = useCallback(() => {
            queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_GROUPS() })
            queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.GET_GROUP() })
            dispatch(globalActions.getMessageStore()).success(mode === MODES.CREATE ? "Group has been created." : `Group ${groupName} has been updated.`)
            onClose()
            mode === MODES.CREATE && navigate(DashboardLinkFactory.dataLake.group({ baseUrl: "", groupName, query: "" }))
          }, [dispatch, groupName, mode, navigate, onClose, queryClient]),

          onSubmit = useCallback(() => {
            const currentDetails = getCurrentDetails({ details, advanced }),
                  dirty = groupDialogService.getDirtyTabs({
                    mode: effectiveMode,
                    initialDetails: baseDetails,
                    details: currentDetails,
                    initialConfig: { groupConfig: advanced.loadedGroupConfig, dynamicValidation: advanced.dynamicValidationFileContent },
                    config: { groupConfig: advanced.groupConfig, dynamicValidation: advanced.dynamicValidation, shouldRemoveInvalidParams: advanced.validationOptions.shouldRemoveInvalidParams },
                    shareChanges: share.shareChanges
                  }),
                  validationErrors = groupDialogService.getValidationErrors({
                    mode: effectiveMode,
                    groupName,
                    priority: advanced.priority,
                    groupConfig: advanced.groupConfig,
                    dynamicValidation: advanced.dynamicValidation,
                    dirty
                  })
            if(validationErrors.length > 0) {
              details.setErrors(validationErrors)
              menuTabsRef.current?.setSelectedTab(validationErrors[0].tab)
              return Promise.resolve()
            }
            if(!isCreate && !dirty.details && !dirty.config && !dirty.share) {
              onClose()
              return Promise.resolve()
            }
            if(isSubmitting.current) {
              return Promise.resolve()
            }
            isSubmitting.current = true
            const requestId = CommonService.generateUID()
            dispatch(globalActions.handleRequest({ id: requestId }))
            const groupStep = isCreate
              ? createGroup().then(() => setCreated({ groupName, details: currentDetails, fields: fieldsToSubmit }))
              : (dirty.details ? editGroup() : Promise.resolve())
            return groupStep
              .then(() => Promise.all([
                dirty.config ? uploadConfig() : Promise.resolve(),
                dirty.share ? saveShares() : Promise.resolve()
              ]))
              .then(onSuccess)
              .catch(error => console.error(error))
              .finally(() => {
                isSubmitting.current = false
                dispatch(globalActions.handleRequest({ id: requestId }))
              })
          }, [advanced, baseDetails, createGroup, details, dispatch, editGroup, effectiveMode, fieldsToSubmit, groupName, isCreate, menuTabsRef, onClose, onSuccess, saveShares, share.shareChanges, uploadConfig])

    return useMemo(() => ({ onSubmit, isCreated: !!created }), [created, onSubmit])
  }

  return {
    useGroupDialogSubmit
  }
})()
